<template>
 <h1 class="font-weight-bold" style="width: 100%; background-color: #f0f0f0; padding: 10px 20px; margin: 0;">
    Incoming SMS Logs
  </h1>
  <PortalTable ref="incoming_sms_index" table-id="incoming_sms_index"
    :table-data="incomingSmsLogs"
    :total-records="smsTotalRecords"
    :page="smsPageNumber"
    :page-count="smsPageCount"
    :loading="isSavableStateProcessing"
    :filter-values="smsFilterValues"
    filter-display="row"
    empty="No record found!"
    @on-sort="handleSortChange"
    @on-page="handlePageChange"
    @on-filter-change="handleFilterChange">
    <template #columns>
     <Column field="from_phone_number" header="From Phone Number" sortable=""
        filter-field="from_phone_number"
        :filter-menu-style="{ width: '14rem' }"
        style="min-width: 14rem"
        :show-filter-menu="false">
        <template #filter="{ filterModel = { value: null }, filterCallback }">
          <InputText v-model="filterModel.value"
            type="text"
            :readonly="isSavableStateProcessing"
            class="p-column-filter form-control"
            placeholder="Search by From Phone #"
            @input="filterCallback()" 
          />
        </template>
      </Column>          
      <Column field="forwarded_phone_number" header="Forwarded Phone #" sortable
        filter-field="forwarded_phone_number"
        :filter-menu-style="{ width: '14rem' }"
        style="min-width: 14rem"
        :show-filter-menu="false">
        <template #filter="{ filterModel = { value: null }, filterCallback }">
          <InputText v-model="filterModel.value"
            type="text"
            :readonly="isSavableStateProcessing"
            class="p-column-filter form-control"
            placeholder="Search by Forwarded Phone #"
            @input="filterCallback()" 
          />
        </template>
      </Column>          
      <Column field="associated_user.email" header="Email" 
        filter-field="user_email"
        :filter-menu-style="{ width: '14rem' }"
        style="min-width: 14rem"
        :show-filter-menu="false">
        <template #filter="{ filterModel = { value: null }, filterCallback }">
          <InputText v-model="filterModel.value"
            type="text"
            :readonly="isSavableStateProcessing"
            class="p-column-filter form-control"
            placeholder="Search by Email"
            @input="filterCallback()" 
          />
        </template>
      </Column>          
      <Column field="associated_user.full_name" header="User Name" style="min-width: 100px;" />  
      <Column field="message_body" header="Message Body" style="min-width: 250px;" />   
      <Column field="handling_status" header="Status" sortable
        filter-field="handling_status"
        :filter-menu-style="{ width: '14rem' }"
        style="min-width: 14rem"
        :show-filter-menu="false">
        <template #filter="{ filterModel = { value: null }, filterCallback }">
          <template v-if="filterModel.value === null">
            <span v-if="(filterModel.value = 'errored')"></span>
          </template>
          <InputText v-model="filterModel.value"
            type="text"
            :readonly="isSavableStateProcessing"
            class="p-column-filter form-control"
            placeholder="Search by Status"
            @input="filterCallback()" 
          />
        </template>
      </Column>        
      <Column field="error" header="Error" style="min-width: 70px;" />                
      <Column field="sms_date_time" header="SMS Date" sortable
        style="min-width: 160px"
        :show-filter-menu="false"
        :filter-menu-style="{ width: '15rem' }">
        <template #body="slotProps">
          {{ slotProps.data.sms_date_time }}
        </template>
        <template #filter>
          <button type="button" class="btn btn-outline-secondary" title="Filter" @click="toggleFilter">
            <font-awesome-icon :icon="['fas', 'fa-filter']" aria-hidden="true" />
          </button>
          <button v-if="hasFilter" class="btn btn-outline-secondary" type="button" label="Clear Filter" title="Clear Filters" @click="clearFilters">
            <div class="fa-layers fa-fw">
              <font-awesome-icon :icon="['fas', 'fa-filter']" aria-hidden="true" /> 
              <font-awesome-icon :icon="['fas', 'slash']" transform="grow-3" />
            </div>
          </button>
          <div v-if="showFilter" class="overlay" @blur="toggleFilter">
            <span>From:</span>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Date</span>
              <InputText ref="from_date"
                v-model="dateFrom"
                type="date"
                class="p-column-filter"
                placeholder="Message Date From"
                @input="filterData" />                
            </div>             
            <span>To:</span>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">Date</span>
              <InputText ref="to_date" v-model="dateTo"
                type="date"
                class="p-column-filter"
                @input="filterData" />               
            </div>
          </div>
        </template>
      </Column>         
    </template>
      </PortalTable>
</template>

<script>
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

export default {
  name: 'IncomingSMSLogsTable',
  components: {
    PortalTable,
    Column,
    InputText,
  },
  props: {
    incomingSmsLogs: Array,  
    smsTotalRecords: Number, 
    smsPageNumber: Number, 
    smsPageCount: Number,  
    isSavableStateProcessing: Boolean, 
    smsFilterValues: Object, 
  },
  data() {
    return {
      search_params: {},
      showFilter: false,
      dateFrom: '',
      dateTo: '',
      localFilters: {},
    };
  },
  mounted() {
    this.localFilters = { ...this.callFilterValues, handling_status: 'errored' };
    this.$emit('filterChange', { handling_status: 'errored' });
  },
  methods: {
    hasFilter() {
      return !!this.dateFrom || !!this.dateTo;
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    filterData() {
      // Emit the filter change event with the updated dates
     this.$emit('filterChange', { ...this.search_params, date_from: this.dateFrom, date_to: this.dateTo });
    },
    clearFilters() {
      // Clear the filter values and emit an empty filter change event
      this.dateFrom = '';
      this.dateTo = '';
      this.showFilter = false;

      // Emit the cleared filter event
      this.$emit('filterChange', { date_from: '', date_to: '' });
    },
    // Emit sorting event to parent
    handleSortChange(params) {
      this.$emit('sortChange', params);
    },

    // Emit pagination event to parent
    handlePageChange(newPage) {
      this.$emit('pageChange', newPage);
    },

    // Emit filter change event to parent
    handleFilterChange(filters) {
      this.search_params = filters;
      this.$emit('filterChange',{ ...filters,
      date_from: this.dateFrom,
      date_to: this.dateTo,
    });
    }
  }
};
</script>
